import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button, Card, ErrorNote, Field, Input, Loading } from '@/components/ui';

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <img src="/icon.svg" alt="" className="size-9" />
          <span className="text-xl font-semibold">BluePrint</span>
        </div>
        <Card className="p-6">
          <h1 className="text-lg font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-fg">{subtitle}</p>}
          <div className="mt-5">{children}</div>
        </Card>
      </div>
    </div>
  );
}

function useSubmit(fn: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

// Only allow same-site relative paths after sign-in.
function safeNext(next: string | null) {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    await api.post('/api/auth/login', { email, password });
    await qc.invalidateQueries({ queryKey: ['me'] });
    nav(safeNext(params.get('next')), { replace: true });
  });
  return (
    <AuthCard title="Sign in">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email"><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
        <Field label="Password"><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <ErrorNote error={error} />
        <Button type="submit" className="w-full" size="lg" loading={busy}>Sign in</Button>
        <p className="text-center text-sm"><Link to="/forgot-password" className="text-info hover:underline">Forgot your password?</Link></p>
      </form>
    </AuthCard>
  );
}

function PasswordFields({ password, setPassword, confirm, setConfirm }: { password: string; setPassword: (v: string) => void; confirm: string; setConfirm: (v: string) => void }) {
  return (
    <>
      <Field label="New password" hint="At least 10 characters. A short phrase is easiest to remember.">
        <Input type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label="Confirm password">
        <Input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
    </>
  );
}

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const info = useQuery({ queryKey: ['invite', token], queryFn: () => api.get<{ email: string; name: string }>(`/api/auth/invite/${token}`), retry: false });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    if (password !== confirm) throw new Error('Passwords do not match.');
    await api.post('/api/auth/invite/accept', { token, password, name: name || undefined });
    await qc.invalidateQueries({ queryKey: ['me'] });
    nav('/', { replace: true });
  });
  if (info.isLoading) return <Loading />;
  if (info.error) return <AuthCard title="Invite link problem"><ErrorNote error={info.error} /></AuthCard>;
  return (
    <AuthCard title="Welcome to BluePrint" subtitle={<>Set a password for <strong>{info.data!.email}</strong>.</>}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Your name"><Input value={name || info.data!.name} onChange={(e) => setName(e.target.value)} /></Field>
        <PasswordFields {...{ password, setPassword, confirm, setConfirm }} />
        <ErrorNote error={error} />
        <Button type="submit" className="w-full" size="lg" loading={busy}>Create account</Button>
      </form>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { busy, error, submit } = useSubmit(async () => {
    await api.post('/api/auth/password/forgot', { email });
    setSent(true);
  });
  return (
    <AuthCard title="Reset your password" subtitle="We'll email you a link that works for one hour.">
      {sent ? (
        <p className="text-sm">If an account exists for <strong>{email}</strong>, a reset link is on its way. <Link to="/login" className="text-info hover:underline">Back to sign in</Link></p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
          <ErrorNote error={error} />
          <Button type="submit" className="w-full" size="lg" loading={busy}>Send reset link</Button>
          <p className="text-center text-sm"><Link to="/login" className="text-info hover:underline">Back to sign in</Link></p>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    if (password !== confirm) throw new Error('Passwords do not match.');
    await api.post('/api/auth/password/reset', { token, password });
    await qc.invalidateQueries({ queryKey: ['me'] });
    nav('/', { replace: true });
  });
  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <PasswordFields {...{ password, setPassword, confirm, setConfirm }} />
        <ErrorNote error={error} />
        <Button type="submit" className="w-full" size="lg" loading={busy}>Save password</Button>
      </form>
    </AuthCard>
  );
}
